import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Layout } from '../components/layout/Layout';
import { PageHero, ContentSection, SEOContent, CTASection } from '../components/shared';
import { pageHeroImages } from '../data/mock';
import { ArrowRight, ArrowDown } from 'lucide-react';

const schmerzFeatures = [
  {
    id: 'ruecken',
    title: 'Rückenschmerzen & Wirbelsäule',
    description: 'Gezielte Behandlung bei Rückenbeschwerden und Wirbelsäulenproblemen.',
    image: '/images/2F_Wirbelsaeule_Behandlung.webp'
  },
  {
    id: 'nacken',
    title: 'Nacken- & Schulterschmerzen',
    description: 'Therapie bei Verspannungen und Schmerzen im Schulter-Nacken-Bereich.',
    image: '/images/Nackenschmerzen_neu1.webp'
  },
  {
    id: 'injektion',
    title: 'Injektionstherapie / Schmerzbehandlung',
    description: 'Gezielte Injektionen zur Schmerzlinderung und Entzündungshemmung.',
    image: '/images/Injektionstherapie.webp'
  },
  {
    id: 'akupunktur',
    title: 'Akupunktur',
    description: 'Traditionelle Heilmethode zur Schmerzlinderung und Entspannung.',
    image: '/images/2F_Akupunktur.webp'
  }
];

export const SchmerzPage = () => {
  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Layout>
      <PageHero
        title="Schmerz & Wirbelsäule im Medicum Diez"
        subtitle="Gezielte Schmerztherapie, Wirbelsäulenbehandlung und Akupunktur – individuell und ganzheitlich."
        backgroundImage={pageHeroImages.schmerz}
        ctaText="Schmerztherapie-Termin buchen"
      />

      {/* Schnellnavigation - Anchor Buttons */}
      <section className="py-6 bg-[#032925]">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={() => scrollToSection('leistungen')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#edd68d] hover:bg-[#d4c17d] rounded-full text-[#032925] text-sm font-medium transition-colors"
            >
              Unsere Leistungen
              <ArrowDown className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollToSection('ruecken')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#66f2ae] hover:bg-[#50d899] rounded-full text-black text-sm font-medium transition-colors"
            >
              Rückenschmerzen
              <ArrowDown className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollToSection('streckbank')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#66f2ae] hover:bg-[#50d899] rounded-full text-black text-sm font-medium transition-colors"
            >
              Streckbank & Spinemed
              <ArrowDown className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollToSection('akupunktur')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#66f2ae] hover:bg-[#50d899] rounded-full text-black text-sm font-medium transition-colors"
            >
              Akupunktur
              <ArrowDown className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollToSection('termin')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#edd68d] hover:bg-[#d4c17d] rounded-full text-[#032925] text-sm font-medium transition-colors"
            >
              Termin buchen
              <ArrowDown className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Feature Grid mit klickbaren Karten */}
      <section id="leistungen" className="py-16 lg:py-20 bg-[#f9f3e2]">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl md:text-3xl font-semibold text-[#032925]">
              Unsere Leistungen bei Schmerzen und Wirbelsäulenbeschwerden
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {schmerzFeatures.map((feature, index) => (
              <motion.div
                key={feature.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#8ae9bb]/50 rounded-2xl overflow-hidden shadow-lg cursor-pointer hover:shadow-xl transition-all"
                onClick={() => scrollToSection(feature.id)}
              >
                <div className="aspect-video overflow-hidden">
                  <img
                    src={feature.image}
                    alt={feature.title}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-[#032925] mb-2">{feature.title}</h3>
                  <p className="text-[#032925]/80 text-sm mb-4">{feature.description}</p>
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#032925] hover:text-[#1a1308]">
                    Mehr erfahren
                    <ArrowDown className="w-4 h-4" />
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Rückenschmerzen Content */}
      <div id="ruecken">
        <ContentSection
          title="Rückenschmerzen & Wirbelsäule"
          content="Rückenschmerzen gehören zu den häufigsten Beschwerden. Wir analysieren die Ursachen durch gründliche Untersuchung und gezielte Diagnostik. Ob Bandscheibenprobleme, Muskelverspannungen oder degenerative Veränderungen – wir entwickeln mit Ihnen einen individuellen Behandlungsplan."
          image="/images/2F_Wirbelsaeule_Behandlung.webp"
          backgroundColor="white"
        />
      </div>

      {/* Nacken Content */}
      <div id="nacken">
        <ContentSection
          title="Nacken- & Schulterschmerzen"
          content="Verspannungen im Schulter-Nacken-Bereich können vielfältige Ursachen haben. Wir kombinieren manuelle Untersuchung, bildgebende Diagnostik und therapeutische Maßnahmen, um Ihre Beschwerden gezielt zu behandeln und langfristig zu lindern."
          image="/images/Nackenschmerzen_neu2.webp"
          imagePosition="left"
          backgroundColor="gray"
        />
      </div>

      {/* Injektion Content */}
      <div id="injektion">
        <ContentSection
          title="Injektionstherapie / Schmerzbehandlung"
          content="Gezielte Injektionen können bei akuten und chronischen Schmerzen schnelle Linderung bringen. Wir setzen lokale Betäubungsmittel, entzündungshemmende Medikamente und weitere Substanzen gezielt ein, um Schmerzen zu reduzieren und die Heilung zu fördern."
          image="/images/Schmerztherapie_1.webp"
          backgroundColor="white"
        />
      </div>

      {/* Ganzheitliche Schmerztherapie */}
      <ContentSection
        title="Ganzheitliche Schmerztherapie – individuell abgestimmt"
        content="Schmerzen können viele Ursachen haben und belasten Körper und Alltag oft erheblich. In unserer Praxis nehmen wir uns Zeit, Ihre Beschwerden genau zu verstehen und gemeinsam mit Ihnen ein persönliches Therapiekonzept zu entwickeln. Ziel ist es, Schmerzen nachhaltig zu lindern, Beweglichkeit zurückzugewinnen und Ihre Lebensqualität spürbar zu verbessern."
        image="/images/Schmerztherapie_2.webp"
        imagePosition="left"
        backgroundColor="gray"
      />

      {/* Streckbank */}
      <div id="streckbank">
        <ContentSection
          title="Streckbank und moderne Wirbelsäulentherapie (Spinemed)"
          content="Zur Entlastung der Wirbelsäule setzen wir bei geeigneten Beschwerden die Streckbank ein. Durch schonende Streckung können Bandscheiben und Nervenstrukturen entlastet und Schmerzen gelindert werden. Ergänzend kommt bei ausgewählten Patientinnen und Patienten die Spinemed-Therapie zum Einsatz – ein computergestütztes System zur gezielten Dekompression der Wirbelsäule, das die Regeneration unterstützen und chronische Rückenschmerzen nachhaltig verbessern kann."
          image="/images/Streckbank_Neu.webp"
          backgroundColor="white"
        />
      </div>

      {/* Akupunktur */}
      <div id="akupunktur">
        <ContentSection
          title="Akupunktur als bewährte Ergänzung"
          content="Akupunktur kann bei vielen Schmerzformen eine wertvolle Ergänzung sein. Sie unterstützt die körpereigenen Heilungsprozesse, lindert Beschwerden und fördert die Entspannung – eingebettet in ein ganzheitliches Behandlungskonzept, das individuell auf Ihre Situation abgestimmt ist."
          image="/images/2F_Akupunktur.webp"
          imagePosition="left"
          backgroundColor="gray"
        />
      </div>

      {/* Zusammenarbeit */}
      <section className="py-16 lg:py-20 bg-[#f9f3e2]">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl md:text-3xl font-semibold mb-6 text-[#032925]">
                Wirbelsäule, Muskulatur und Gelenke gezielt behandeln
              </h2>
              <p className="text-[#032925]/80 leading-relaxed mb-6">
                Bei Rücken-, Nacken- und Wirbelsäulenbeschwerden kombinieren wir sorgfältige klinische Untersuchung, moderne Diagnostik und bewährte Therapieformen. Neben medikamentöser Behandlung und Injektionstherapie setzen wir auch physikalische Verfahren ein, um Verspannungen zu lösen, Entzündungen zu reduzieren und die natürliche Beweglichkeit zu fördern.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/leistungen/diagnostik" className="inline-flex items-center gap-2 px-6 py-3 bg-[#8ae9bb]/50 hover:bg-[#8ae9bb]/70 rounded-full text-[#032925] font-medium transition-colors">
                  Diagnostik-Zentrum
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="/images/MRT 5.webp"
                  alt="Diagnostik"
                  className="w-full h-auto object-cover aspect-[4/3]"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <SEOContent
        title="Schmerztherapie und Wirbelsäulenbehandlung in Diez und Limburg"
        content="Im Medicum Diez behandeln wir Rückenschmerzen, Nackenbeschwerden, Wirbelsäulenprobleme und chronische Schmerzen. Unsere Hausärzte bieten Injektionstherapie, Akupunktur, Streckbank und moderne Diagnostik für eine gezielte Schmerztherapie im Rhein-Lahn-Kreis."
      />

      <div id="termin">
        <CTASection
          title="Termin zur Schmerztherapie vereinbaren"
          subtitle="Vereinbaren Sie Ihren Termin zur Abklärung und Behandlung von Schmerzen online oder telefonisch."
          buttonText="Schmerztherapie-Termin buchen"
        />
      </div>
    </Layout>
  );
};

export default SchmerzPage;
